/**
 * What to tell people when something fails.
 *
 * The rule: a person sees one calm sentence, and - when the fault is ours - a
 * short reference they can quote. The cause goes to the logs (the server's own,
 * and `error-reporting.ts` for what only the browser saw), never onto the page.
 *
 * Our API's 4xx details are written for people ("That folder is not yours to
 * delete"), so they are shown as they are. Anything that reads like a machine
 * wrote it - an exception name, SQL, a stack frame, pydantic's list of objects -
 * is replaced by the sentence for its status. `looksTechnical` is that test.
 *
 * No imports, on purpose: the API client uses this, and the reporter plugs in
 * through `setErrorReporter` rather than being imported here.
 */

export const GENERIC_ERROR = 'Something went wrong. Please try again.';

/** The sentence for a status, when the server said nothing we would show. */
export function statusMessage(status: number): string {
  if (status === 0) return "Can't reach the server. Check your connection and try again.";
  if (status === 401) return 'Your sign-in has expired. Sign in again to continue.';
  if (status === 403) return "You don't have access to do that.";
  if (status === 404) return 'That could not be found. It may have been moved or deleted.';
  if (status === 408 || status === 504) {
    return 'The server took too long to answer. Please try again, or ask for less at once.';
  }
  if (status === 409) return 'That changed while you were working on it. Reload and try again.';
  if (status === 413) return 'That is too large to handle in one go.';
  if (status === 429) return 'Too many requests just now. Please wait a moment and try again.';
  if (status === 400 || status === 422) {
    return 'Some of the settings were not accepted. Check them and try again.';
  }
  if (status === 502 || status === 503) {
    return 'The service is temporarily unavailable. Please try again in a moment.';
  }
  if (status >= 500) return 'Something went wrong on our side. Please try again in a moment.';
  return GENERIC_ERROR;
}

// Each of these is something a person was never meant to read.
const TECHNICAL: RegExp[] = [
  /\b[A-Z][A-Za-z]*(?:Error|Exception)\b/, // TypeError, BinderException, OperationalError
  /Traceback \(most recent call last\)/,
  /^\s*at .+\(.+:\d+:\d+\)/m, // a JS stack frame
  /File ".+", line \d+/, // a Python one
  /\b(?:SELECT|INSERT|UPDATE|DELETE)\b[\s\S]*\b(?:FROM|INTO|SET|WHERE)\b/,
  /\b(?:psycopg|duckdb|botocore|sqlalchemy|pydantic|asyncpg|uvicorn|starlette)\b/i,
  /\b(?:Binder|Catalog|Parser|Conversion|IO) Error\b/,
  /Candidate bindings/,
  /\[object Object\]/,
  /\bundefined is not\b|\bis not a function\b|Cannot read propert/,
  /Unexpected (?:token|end of JSON)/,
  /\bs3:\/\/|\/home\/|\/usr\/|\\Users\\|\.py\b/,
  /\bNoneType\b|\bnull value in column\b|\bviolates .* constraint\b/,
];

/** Whether `text` reads as if a machine wrote it for another machine. */
export function looksTechnical(text: string): boolean {
  if (text.length > 400) return true;
  return TECHNICAL.some((pattern) => pattern.test(text));
}

/**
 * The message for a failed API call.
 *
 * `detail` is what the server said (if it was a string), `ref` its reference.
 * Server faults (5xx) always carry the reference when there is one, so a
 * screenshot is enough to find the log line.
 */
export function apiErrorMessage(status: number, detail: string | null, ref: string | null): string {
  const said = detail?.trim();
  let message = said && !looksTechnical(said) ? said : statusMessage(status);
  if (status >= 500 && ref && !message.includes(ref)) message = `${message} (Reference: ${ref})`;
  return message;
}

type Reporter = (error: unknown, context?: string) => void;
let reporter: Reporter | null = null;

/** Where `userMessage` sends errors it hides. Set by `error-reporting.ts`. */
export function setErrorReporter(next: Reporter | null): void {
  reporter = next;
}

/** The engine's own errors: nobody writes these on purpose for a person to read. */
const BUG_TYPES = [TypeError, ReferenceError, SyntaxError, RangeError, URIError, EvalError];

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * A sentence for any caught error - the one thing a `catch` should show.
 *
 * An API failure already carries its message (the client wrote it with
 * `apiErrorMessage`). Any other `Error` is our own bug - a TypeError in a
 * render, a JSON parse - so the person gets `fallback` and the error goes to the
 * log, tagged with `context` ("save dashboard") so it can be found. A plain
 * string an app module threw on purpose is shown if it reads like a sentence.
 */
export function userMessage(error: unknown, fallback: string = GENERIC_ERROR, context?: string): string {
  if (error && typeof error === 'object' && (error as { name?: unknown }).name === 'ApiError') {
    const message = (error as Error).message;
    return message && !looksTechnical(message) ? message : fallback;
  }
  if (isAbort(error)) return fallback;
  if (error instanceof Error) {
    // Errors the app raises for people - `new Error('Pick a ship first.')`, a
    // `DashboardConflictError` - are sentences. The engine's own (TypeError,
    // ReferenceError ...) come from bugs, and so does anything technical.
    if (!BUG_TYPES.some((type) => error instanceof type) && error.message && !looksTechnical(error.message)) {
      return error.message;
    }
    reporter?.(error, context);
    return fallback;
  }
  if (typeof error === 'string' && error && !looksTechnical(error)) return error;
  reporter?.(error, context);
  return fallback;
}
